import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function SubscribePage() {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    document.title = 'Subscribe to The BOLO Report | Weekly Reseller Intelligence';

    // Meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Subscribe to The BOLO Report and get weekly reseller trend intelligence every Thursday. Trending brands, resale value ranges, marketplace insights, and early signals delivered to your inbox.');

    // Canonical
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${siteUrl}/subscribe`);

    // Schema.org JSON-LD
    const id = 'schema-subscribe';
    if (!document.getElementById(id)) {
      const script = document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      script.text = JSON.stringify([
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Subscribe to The BOLO Report',
          url: `${siteUrl}/subscribe`,
          description: 'Subscribe to The BOLO Report and get weekly reseller trend intelligence every Thursday. Trending brands, resale value ranges, marketplace insights, and early signals delivered to your inbox.',
          isPartOf: {
            '@type': 'WebSite',
            name: 'The BOLO Report',
            url: siteUrl,
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'NewsletterSubscription',
          name: 'The BOLO Report Newsletter',
          url: `${siteUrl}/subscribe`,
          description: 'Weekly reseller trend intelligence newsletter published every Thursday.',
          provider: {
            '@type': 'Organization',
            name: 'The BOLO Report',
            url: siteUrl,
          },
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
            description: 'Free weekly newsletter subscription',
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What is The BOLO Report?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'The BOLO Report is a weekly reseller trend intelligence newsletter published every Thursday. It covers trending brands, expected resale value ranges, marketplace insights, and early signals from the resale market.',
              },
            },
            {
              '@type': 'Question',
              name: 'How often is The BOLO Report published?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'The BOLO Report is published every Thursday and delivered directly to your inbox.',
              },
            },
            {
              '@type': 'Question',
              name: 'Which platforms does The BOLO Report cover?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'The BOLO Report covers major resale platforms including eBay, Poshmark, and Whatnot, providing actionable reseller trend intelligence across all of them.',
              },
            },
          ],
        },
      ]);
      document.head.appendChild(script);
    }

    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
      if (canonical) canonical.setAttribute('href', `${siteUrl}/`);
    };
  }, []);

  const benefits = [
    'Trending brands to source',
    'Expected resale value ranges',
    'Marketplace insights',
    'Early signals from the resale market',
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-20">
        <section className="relative min-h-[65vh] flex items-center justify-center bg-[#272727]">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] bg-[#4d4d31]/20 rounded-full blur-[120px] pointer-events-none"></div>
          <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
            <h1 className="font-antonio text-5xl lg:text-6xl font-bold text-[#e9eae4] mb-12 leading-tight uppercase">
              Get the BOLO Report Every Thursday
            </h1>
            <div className="space-y-6 mb-12 max-w-2xl mx-auto">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-4 text-left">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#4d4d31]/40 flex-shrink-0">
                    <i className="ri-check-line text-[#e9eae4] text-lg"></i>
                  </div>
                  <span className="font-barlow text-xl text-[#e9eae4]/85">{benefit}</span>
                </div>
              ))}
            </div>
            <a
              href="https://theboloreport.substack.com/subscribe?utm_source=menu&simple=true&next=https%3A%2F%2Ftheboloreport.substack.com%2F"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-block bg-[#e9eae4] text-[#272727] px-10 py-5 rounded-full text-lg font-bold hover:bg-white transition-all hover:-translate-y-1 whitespace-nowrap cursor-pointer font-barlow tracking-wide"
            >
              Subscribe on Substack
            </a>
          </div>
        </section>

        <section className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <img
                  src="https://public.readdy.ai/ai/img_res/11b92038b861ca1052db952eef80ccc3.jpg"
                  alt="Newsletter Preview"
                  className="w-full h-auto rounded-3xl"
                />
              </div>
              <div>
                <h2 className="font-antonio text-4xl lg:text-5xl font-bold text-[#272727] mb-8 uppercase">
                  What's Inside Each Issue
                </h2>
                <div className="space-y-8">
                  {[
                    { icon: 'ri-bar-chart-box-line', title: 'Market Data Analysis', desc: 'Real numbers from actual sales across multiple platforms. No guesswork—just data.' },
                    { icon: 'ri-search-eye-line', title: 'Sourcing Guidance', desc: 'Exactly what to look for, where to find it, and what price points make sense.' },
                    { icon: 'ri-lightbulb-flash-line', title: 'Early Trend Signals', desc: 'Spot emerging opportunities before they become saturated. Get there first.' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start space-x-4">
                      <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-4xl text-[#4d4d31]`}></i>
                      </div>
                      <div>
                        <h3 className="font-antonio text-xl font-bold text-[#272727] mb-2 uppercase">{item.title}</h3>
                        <p className="font-barlow text-base text-[#272727]/60 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#e9eae4] py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="font-antonio text-4xl font-bold text-[#272727] mb-6 uppercase">Join Hundreds of Resellers</h2>
            <p className="font-barlow text-xl text-[#272727]/60 mb-10 leading-relaxed">
              Get actionable intelligence delivered to your inbox every Thursday. No fluff, no filler—just the trends that matter.
            </p>
            <a
              href="https://theboloreport.substack.com/subscribe?utm_source=menu&simple=true&next=https%3A%2F%2Ftheboloreport.substack.com%2F"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-block bg-[#4d4d31] text-[#e9eae4] px-10 py-5 rounded-full text-lg font-bold hover:bg-[#3d3d25] transition-all hover:-translate-y-1 whitespace-nowrap cursor-pointer font-barlow tracking-wide"
            >
              Start Your Subscription
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
